import React, { FC, useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { Button, ButtonVariant } from 'components/common/buttons/Button';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import { IndividualPayPalMerchantAccount } from 'api/admin/businesses/getPayPalMerchantAccounts';
import { ConfirmPayPalActionDialog } from '../../businesses/paypalAccounts/ConfirmPayPalActionDialog';
import { PayPalLoadError } from '../../businesses/paypalAccounts/PayPalLoadError';
import {
  getPayPalPayerIdLabel,
  getPayPalTransactionsReadyLabel,
} from '../../businesses/paypalAccounts/paypalAccountLabels';
import { usePayPalMerchantAccounts } from '../../businesses/paypalAccounts/queries/usePayPalMerchantAccounts';
import styles from './PayPalAccount.module.scss';
import { useAssignPayPalStoreAccount } from './mutations/useAssignPayPalStoreAccount';
import { useUnassignPayPalStoreAccount } from './mutations/useUnassignPayPalStoreAccount';
import { usePayPalStoreAccount } from './queries/usePayPalStoreAccount';

const PENDING_ACTIONS = {
  assign: 'assign',
  unassign: 'unassign',
} as const;

type PendingAction = (typeof PENDING_ACTIONS)[keyof typeof PENDING_ACTIONS];

const getMerchantAccountLabel = ({
  id,
  payerId,
  transactionsReady,
}: Pick<IndividualPayPalMerchantAccount, 'id' | 'payerId' | 'transactionsReady'>): string =>
  `#${id} · ${getPayPalPayerIdLabel(payerId)} · ${getPayPalTransactionsReadyLabel(
    transactionsReady,
  )}`;

export const PayPalAccount: FC<{
  storeId: number;
  businessId: number;
}> = ({ storeId, businessId }) => {
  const {
    isLoading: isAssignmentLoading,
    error: assignmentError,
    data: assignment,
    refetch: refetchAssignment,
  } = usePayPalStoreAccount(storeId);
  const {
    isLoading: isMerchantAccountsLoading,
    error: merchantAccountsError,
    data: merchantAccounts,
    refetch: refetchMerchantAccounts,
  } = usePayPalMerchantAccounts(businessId);
  const { mutateAsync: assignAccount, isLoading: isAssigning } = useAssignPayPalStoreAccount();
  const { mutateAsync: unassignAccount, isLoading: isUnassigning } =
    useUnassignPayPalStoreAccount();

  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  const selectedAccount = useMemo(
    () => merchantAccounts.find(({ id }) => id === selectedAccountId) ?? null,
    [merchantAccounts, selectedAccountId],
  );
  // A store routed to an account PayPal has not enabled cannot get a client token for its device,
  // so only ready accounts are assignable.
  const canAssign =
    !!selectedAccount?.transactionsReady &&
    selectedAccount.id !== assignment?.paypalMerchantAccountId;
  const hasNotReadyAccounts = merchantAccounts.some(({ transactionsReady }) => !transactionsReady);

  const handleSuccess = async () => {
    setPendingAction(null);
    setSelectedAccountId(null);
    // Assigned store counts on the business's merchant accounts change with every assignment.
    await Promise.all([refetchAssignment(), refetchMerchantAccounts()]);
  };

  const handleConfirm = async () => {
    const isAssign = pendingAction === PENDING_ACTIONS.assign && canAssign;
    const isUnassign = pendingAction === PENDING_ACTIONS.unassign;
    if (!isAssign && !isUnassign) {
      return;
    }

    try {
      if (isAssign) {
        await assignAccount({ storeId, paypalMerchantAccountId: selectedAccount.id });
      } else {
        await unassignAccount(storeId);
      }
    } catch {
      // The mutation's onError has already surfaced the failure; keep the dialog open to retry.
      return;
    }

    await handleSuccess();
  };

  if (isAssignmentLoading || isMerchantAccountsLoading) {
    return <CentsLoader />;
  }

  // Without both, the panel would show a failed load as "no assignment" or "no accounts" and let
  // the operator replace an assignment they cannot see.
  if (assignmentError || merchantAccountsError) {
    return (
      <div className="page-container">
        <PayPalLoadError
          message={
            assignmentError
              ? "Couldn't load this store's PayPal account. Its current assignment is unknown, so it can't be changed."
              : "Couldn't load this business's PayPal merchant accounts."
          }
          onRetry={() => {
            if (assignmentError) {
              refetchAssignment();
            }
            if (merchantAccountsError) {
              refetchMerchantAccounts();
            }
          }}
        />
      </div>
    );
  }

  const assignedAccountLabel = assignment
    ? getMerchantAccountLabel({ ...assignment, id: assignment.paypalMerchantAccountId })
    : null;
  const assignmentMessage = selectedAccount
    ? `PayPal payments for this store will go to merchant account ${getMerchantAccountLabel(
        selectedAccount,
      )}${assignedAccountLabel ? ` instead of ${assignedAccountLabel}` : ''}.`
    : '';

  return (
    <div className="page-container">
      <div className={styles.container}>
        <div className={styles.row}>
          <p className={styles.label}>Assigned Merchant Account</p>
          {assignment ? (
            <div className={styles.assignment}>
              <p>{assignedAccountLabel}</p>
              <Button
                type={ButtonVariant.OUTLINED}
                onClick={() => setPendingAction(PENDING_ACTIONS.unassign)}
              >
                Unassign
              </Button>
            </div>
          ) : (
            <p>None. This store cannot take PayPal payments.</p>
          )}
        </div>
        <div className={styles.row}>
          <p className={styles.label}>{assignment ? 'Change to' : 'Assign'}</p>
          {merchantAccounts.length ? (
            <div className={styles.assignment}>
              <Autocomplete
                disablePortal
                id="autocomplete-choose-paypal-merchant-account"
                options={merchantAccounts}
                getOptionLabel={getMerchantAccountLabel}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionDisabled={({ transactionsReady }) => !transactionsReady}
                value={selectedAccount}
                onChange={(_event, value) => setSelectedAccountId(value?.id ?? null)}
                sx={{ width: '400px' }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select a merchant account" />
                )}
              />
              <Button
                type={ButtonVariant.CONTAINED}
                isDisabled={!canAssign}
                onClick={() => setPendingAction(PENDING_ACTIONS.assign)}
              >
                {assignment ? 'Reassign' : 'Assign'}
              </Button>
            </div>
          ) : (
            <p>This business has no PayPal merchant accounts. Connect one from Business Manager.</p>
          )}
          {hasNotReadyAccounts && (
            <p className={styles.hint}>
              Accounts that are not ready cannot be assigned until the merchant finishes onboarding
              and PayPal enables payments.
            </p>
          )}
        </div>
      </div>
      <ConfirmPayPalActionDialog
        open={pendingAction === PENDING_ACTIONS.assign}
        title={assignment ? 'Reassign PayPal account?' : 'Assign PayPal account?'}
        confirmLabel={assignment ? 'Reassign' : 'Assign'}
        confirmColor="primary"
        isSubmitting={isAssigning}
        onCancel={() => setPendingAction(null)}
        onConfirm={handleConfirm}
      >
        {assignmentMessage}
      </ConfirmPayPalActionDialog>
      <ConfirmPayPalActionDialog
        open={pendingAction === PENDING_ACTIONS.unassign}
        title="Unassign PayPal account?"
        confirmLabel="Unassign"
        isSubmitting={isUnassigning}
        onCancel={() => setPendingAction(null)}
        onConfirm={handleConfirm}
      >
        This store will stop taking PayPal payments until another merchant account is assigned.
      </ConfirmPayPalActionDialog>
    </div>
  );
};
